import { useRef, useState } from 'react';
import { useWorkspaces } from '../../lib/workspaces';
import { WorkspaceBadgeEditor } from './WorkspaceBadgeEditor';
import { WorkspaceNameEditor, WorkspaceNameEditorHandle } from './WorkspaceNameEditor';
import { PageHeader } from './PageHeader';
import { SetupQuestionsControl } from './SetupQuestionsControl';
import { useSpacePresence } from './website/SpacePresence';
import { useSpaceCardEnabled } from '../../lib/space-card-flag';
import styles from './WorkspaceHero.module.css';

// The shared top-of-screen chrome for a workspace's Dashboard: the standard page header (badge + editable name),
// carrying the Space's online presence (`website/SpacePresence.tsx`): its quiet line of
// links under the name, its one next-step pill on the title row, and a suggested-website line under the header — or,
// when the server describes the site (`presence`), the SITE STRIP (`website/SiteStrip.tsx`) as its own row under the
// header, ending in Manage.
//
// Looks the workspace up by slug so a keep-alive
// dashboard host (which only knows the slug) still renders the right hero. The hero owns the 48px gap to
// page content.
export function WorkspaceHero({ slug, page }: { slug: string; page: string }) {
  const workspaces = useWorkspaces();
  const workspace = workspaces.status === 'ready' ? workspaces.workspaces.find((w) => w.slug === slug) : undefined;
  const name = workspace?.name || slug;
  // The badge's edit state is the name field's state, drawn one slot to the left, so the hero owns the
  // flag and hands the field a way to raise it.
  const [editing, setEditing] = useState(false);
  // An image-update failure belongs beside the name, not on the badge — same placement as native.
  const [imageError, setImageError] = useState<string | null>(null);
  /**
   * True while the badge's file dialog is open (or its upload is in flight). The name field stops
   * committing on blur for that window: opening the OS dialog takes focus off the page, and blur is
   * what closes the field — so without this, clicking "Upload a photo" tore down the whole edit
   * state before the user had chosen anything.
   */
  const [picking, setPicking] = useState(false);
  const nameRef = useRef<WorkspaceNameEditorHandle>(null);
  const presence = useSpacePresence(workspace, page);
  // The Space card (spec 2026-10-29) has no "Complete your setup" on the Space; it stays on Home.
  const spaceCardOn = useSpaceCardEnabled();
  // The site strip takes its own row under the header at every width (a 2-column grid on a phone). Never the title
  // row's end: that corner is where AppFrame pins the Agents island ("Yarden is working"),
  // which drew over the buttons, and the slot squeezed the primary's label ("Get clayember.c…").
  const actionsBelow = presence.strip === true;
  /** Set by a successful save, so ending the pick doesn't fight the close by grabbing focus back. */
  const savedRef = useRef(false);
  return (
    <div className={styles.hero}>
      <PageHeader
        flush
        avatar={
          workspace ? (
            <WorkspaceBadgeEditor
              workspace={workspace}
              editing={editing}
              page={page}
              onError={setImageError}
              onPickingChange={(next) => {
                setPicking(next);
                // Back from the dialog with nothing saved: the field is still open but the dialog
                // took its focus, so put the caret back and let the user carry on typing.
                if (!next && !savedRef.current) nameRef.current?.focus();
                if (!next) savedRef.current = false;
              }}
              onSaved={() => {
                savedRef.current = true;
                // A photo actually landed — finish the edit the way clicking away would.
                nameRef.current?.commit();
              }}
            />
          ) : undefined
        }
        title={
          <div className={styles.titleStack}>
            <WorkspaceNameEditor
              ref={nameRef}
              id={workspace?.id}
              name={name}
              page={page}
              onEditingChange={setEditing}
              suspendBlurCommit={picking}
              menu={spaceCardOn}
            />
            {/* On every Space: the questions are about the whole account, not this Space. Not under the Space card. */}
            {!spaceCardOn && <SetupQuestionsControl pageName={page} workspaceId={workspace?.id} />}
            {presence.line}
          </div>
        }
        actions={actionsBelow ? undefined : (presence.pill ?? undefined)}
        actionsAlign="center"
      />
      {imageError && <p className={styles.imageError}>{imageError}</p>}
      {actionsBelow && presence.pill && <div className={styles.actionsBelow}>{presence.pill}</div>}
      {presence.candidate}
    </div>
  );
}
